import { Checkbox, Form } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import {
  AuthSubmitButton,
  EmailField,
  PasswordField,
} from '@/components/auth';
import styles from '@/components/auth/AuthForm.module.scss';
import { REMEMBER_ME_KEY, REMEMBERED_EMAIL_KEY } from '@/config/app';
import { ROUTES } from '@/constants/routes';
import { useMutationReset } from '@/hooks/useMutationReset';
import { useNotify } from '@/hooks/useNotify';
import { useLoginMutation } from '@/modules/auth/authApi';
import { setCredentials } from '@/store/authSlice';
import { useAppDispatch } from '@/store/hooks';
import { setSidebarCollapsed } from '@/store/uiSlice';
import type { LoginPayload, User } from '@/types/auth';
import { resolveMutationResult } from '@/utils/apiResponse';
import { requiredPasswordRule } from '@/utils/validation';

type LoginFormValues = LoginPayload & { rememberMe: boolean };

function getRememberMeDefaults(): LoginFormValues {
  const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  const username = rememberMe ? (localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? '') : '';
  return { username, password: '', rememberMe };
}

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const notify = useNotify();
  const [login, { isLoading, reset }] = useLoginMutation();
  const { control, handleSubmit } = useForm<LoginFormValues>({
    defaultValues: getRememberMeDefaults(),
  });

  useMutationReset(reset);

  const onSubmit = async ({ rememberMe, ...values }: LoginFormValues) => {
    if (rememberMe) {
      localStorage.setItem(REMEMBER_ME_KEY, 'true');
      localStorage.setItem(REMEMBERED_EMAIL_KEY, values.username);
    } else {
      localStorage.removeItem(REMEMBER_ME_KEY);
      localStorage.removeItem(REMEMBERED_EMAIL_KEY);
    }

    const result = await resolveMutationResult<User>(login(values));

    if (!result.ok) {
      notify.error(result.message);
      return;
    }

    dispatch(setCredentials(result.data));
    dispatch(setSidebarCollapsed(true));
    navigate(ROUTES.DASHBOARD, { replace: true });
  };

  return (
    <Form layout="vertical" onFinish={handleSubmit(onSubmit)} className={styles.form} requiredMark>
      <EmailField control={control} name="username" />
      <PasswordField
        control={control}
        name="password"
        label="Password"
        rules={requiredPasswordRule}
      />

      <Form.Item className={styles.actionsRow}>
        <Controller
          name="rememberMe"
          control={control}
          render={({ field }) => (
            <Checkbox
              checked={field.value}
              onChange={(event) => field.onChange(event.target.checked)}
            >
              Remember me
            </Checkbox>
          )}
        />
        <Link to={ROUTES.FORGOT_PASSWORD} className={styles.forgotLink}>
          Forgot password?
        </Link>
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        <AuthSubmitButton label="Sign In" loading={isLoading} />
      </Form.Item>
    </Form>
  );
}
